/**
 * RecurrenceBadge — displays the recurrence summary on a task card.
 * Shows: "Every Monday", "3 days after completion", streak info.
 *
 * Streak tiers use Lucide icons (not emoji) with achievement-style progression:
 *   Sprout → Flame → Zap → Gem → Crown → Trophy
 */

import { memo } from "react"
import dayjs from "dayjs"
import { humanReadableRule } from "@totodo/operations"
import type { DbRecurrence } from "@totodo/operations"
import { cn } from "@/shared/utils/general/cn"
import { Sprout, Flame, Zap, Gem, Crown, Trophy } from "lucide-react"
import type { LucideIcon } from "lucide-react"

interface RecurrenceBadgeProps {
  recurrence: DbRecurrence
  className?: string
  showStreak?: boolean
  /** Completion rate 0-100 (from RecurrenceHistory) */
  completionRate?: number | null
}

/** Achievement tier based on streak + completion rate */
interface StreakTier {
  icon: LucideIcon
  color: string        // text color
  bg: string           // badge background
  darkColor: string
  darkBg: string
}

function getStreakTier(streak: number, rate: number | null | undefined): StreakTier {
  const r = rate ?? 100
  if (streak >= 60 && r >= 90)
    return { icon: Trophy, color: "text-yellow-600", bg: "bg-yellow-100", darkColor: "dark:text-yellow-300", darkBg: "dark:bg-yellow-900/30" }
  if (streak >= 30 && r >= 90)
    return { icon: Crown, color: "text-purple-600", bg: "bg-purple-100", darkColor: "dark:text-purple-300", darkBg: "dark:bg-purple-900/30" }
  if (streak >= 14 && r >= 85)
    return { icon: Gem, color: "text-cyan-600", bg: "bg-cyan-100", darkColor: "dark:text-cyan-300", darkBg: "dark:bg-cyan-900/30" }
  if (streak >= 7 && r >= 80)
    return { icon: Zap, color: "text-amber-600", bg: "bg-amber-100", darkColor: "dark:text-amber-300", darkBg: "dark:bg-amber-900/30" }
  if (streak >= 3)
    return { icon: Flame, color: "text-orange-600", bg: "bg-orange-100", darkColor: "dark:text-orange-300", darkBg: "dark:bg-orange-900/30" }
  return { icon: Sprout, color: "text-green-600", bg: "bg-green-100", darkColor: "dark:text-green-300", darkBg: "dark:bg-green-900/30" }
}

export const RecurrenceBadge = memo(function RecurrenceBadge({
  recurrence,
  className,
  showStreak = true,
  completionRate,
}: RecurrenceBadgeProps) {
  const isFlexible = !!recurrence.flexibleCount
  const summary = isFlexible
    ? `${recurrence.flexibleCount}× per ${recurrence.flexiblePeriod}`
    : humanReadableRule(recurrence.rrule, recurrence.mode, recurrence.afterCompletionDays)

  const flexCompleted = recurrence.flexibleCompletedThisPeriod ?? 0
  const flexGoal = recurrence.flexibleCount ?? 0

  const streak = recurrence.currentStreak ?? 0
  const tier = getStreakTier(streak, completionRate)
  const TierIcon = tier.icon

  return (
    <div className={cn("flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground", className)}>
      <span className="opacity-70">↻</span>
      <span>{summary}</span>
      {/* Flexible progress dots */}
      {isFlexible && (
        <span className="ml-1 flex items-center gap-0.5">
          {Array.from({ length: flexGoal }, (_, i) => (
            <span
              key={i}
              className={cn(
                "size-1.5 rounded-full",
                i < flexCompleted ? "bg-green-500" : "bg-muted-foreground/30",
              )}
            />
          ))}
          <span className="ml-0.5 text-[10px] tabular-nums">
            {flexCompleted}/{flexGoal}
          </span>
        </span>
      )}
      {showStreak && streak >= 2 && (
        <span className={cn(
          "ml-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-semibold",
          tier.bg, tier.color, tier.darkBg, tier.darkColor,
        )}>
          <TierIcon className="size-3" strokeWidth={2.5} />
          {streak}
        </span>
      )}
      {/* Progressive overload target */}
      {recurrence.progressiveField && recurrence.progressiveCurrent != null && (
        <span className="ml-1 rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
          🎯 {recurrence.progressiveCurrent}{recurrence.progressiveUnit ?? ""}
          {recurrence.progressiveMax != null && recurrence.progressiveCurrent >= recurrence.progressiveMax && " 🏆"}
        </span>
      )}
      {recurrence.pausedAt && (
        <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
          ⏸ {dayjs(Number(recurrence.pausedAt)).format("MMM D")}
        </span>
      )}
    </div>
  )
})
